/**
 * Automatyczny system rabatowy - wspoldzielona, czysta logika wyboru rabatu.
 *
 * UWAGA: ten plik jest lustrzanie kopiowany pomiedzy backendem a frontendem:
 *   backend/src/utils/discount.ts
 *   frontend/src/logic/utils/discount.ts
 * Obie kopie musza pozostac identyczne, aby podglad w koszyku i autorytatywne
 * wyliczenie po stronie serwera nigdy sie nie rozjechaly.
 *
 * Konwencje:
 * - kwoty w groszach (liczby calkowite),
 * - procenty w setnych czesciach procenta (basis points): 500 = 5%,
 *   tak samo jak Voucher.percentage,
 * - daty jako "wall clock" w strefie aplikacji (Europe/Warsaw); funkcje
 *   dostaja aktualny czas jako parametr, wiec sam modul nie zalezy od strefy.
 */

export type AutoDiscountType = 'days' | 'cartValue';

export type AutoDiscountRule = {
  id: number;
  type: AutoDiscountType;
  /** liczba dni dla 'days', grosze dla 'cartValue' */
  threshold: number;
  /** basis points, 500 = 5% */
  percentage: number;
  /** poczatek obowiazywania, np. '2026-01-01' lub '2026-01-01T10:00' */
  dateFrom?: string | null;
  /** koniec obowiazywania; sama data oznacza koniec dnia */
  dateTo?: string | null;
  active: boolean;
};

export type AutoDiscountConfig = {
  enabled: boolean;
  daysEnabled: boolean;
  valueEnabled: boolean;
  rules: AutoDiscountRule[];
};

export type AutoDiscountInput = {
  /** liczba dni objetych zamowieniem */
  daysCount: number;
  /** wartosc produktow w koszyku (grosze, bez opakowan, sztuccow i dostawy) */
  cartValue: number;
  /** aktualny czas w strefie aplikacji */
  now: Date;
};

export type AutoDiscountSelection = {
  rule: AutoDiscountRule;
  percentage: number;
};

export type AutoDiscountHint = {
  type: AutoDiscountType;
  percentage: number;
  threshold: number;
  /** ile brakuje: groszy dla 'cartValue', dni dla 'days' */
  missing: number;
};

/** Snapshot rabatu zapisywana w zamowieniu (Order.autoDiscountInfo). */
export type AutoDiscountSnapshot = {
  applied: boolean;
  suppressedByVoucher?: boolean;
  ruleId?: number;
  type?: AutoDiscountType;
  threshold?: number;
  percentage?: number;
  /** podstawa naliczenia w groszach */
  base?: number;
  /** wartosc rabatu w groszach */
  amount?: number;
};

export const hasAppliedAutoDiscount = (amount?: number | null, snapshot?: string | null): boolean => {
  if ((amount ?? 0) > 0) {
    return true;
  }
  if (!snapshot) {
    return false;
  }
  try {
    return (JSON.parse(snapshot) as AutoDiscountSnapshot).applied === true;
  } catch {
    return false;
  }
};

export const AUTO_DISCOUNT_FULL_PERCENTAGE = 10000;

export const DEFAULT_AUTO_DISCOUNT_RULES: AutoDiscountRule[] = [
  { id: 1, type: 'days', threshold: 5, percentage: 500, active: true },
  { id: 2, type: 'days', threshold: 10, percentage: 1000, active: true },
  { id: 3, type: 'cartValue', threshold: 50000, percentage: 500, active: true },
  { id: 4, type: 'cartValue', threshold: 100000, percentage: 1000, active: true },
];

export const EMPTY_AUTO_DISCOUNT_CONFIG: AutoDiscountConfig = {
  enabled: false,
  daysEnabled: false,
  valueEnabled: false,
  rules: [],
};

const WALL_CLOCK = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?$/;

/**
 * Zamienia zapis daty na czas lokalny (wall clock). Ewentualne przesuniecie
 * strefy w zapisie jest ignorowane - regula obowiazuje wg zegara aplikacji.
 */
export const parseWallClock = (value: string | null | undefined, endOfDayWhenDateOnly = false): number | undefined => {
  if (value === undefined || value === null || `${value}`.trim() === '') {
    return undefined;
  }
  const match = WALL_CLOCK.exec(`${value}`.trim());
  if (!match) {
    return undefined;
  }
  const [, year, month, day, hour, minute, second] = match;
  const hasTime = hour !== undefined;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    hasTime ? Number(hour) : (endOfDayWhenDateOnly ? 23 : 0),
    hasTime ? Number(minute) : (endOfDayWhenDateOnly ? 59 : 0),
    hasTime ? Number(second ?? '0') : (endOfDayWhenDateOnly ? 59 : 0),
    hasTime ? 0 : (endOfDayWhenDateOnly ? 999 : 0)
  );
  const time = date.getTime();
  if (isNaN(time)) {
    return undefined;
  }
  const isRealDate = date.getFullYear() === Number(year)
    && date.getMonth() === Number(month) - 1
    && date.getDate() === Number(day);
  return isRealDate ? time : undefined;
};

const isInteger = (value: unknown): value is number =>
  typeof value === 'number' && isFinite(value) && Math.floor(value) === value;

export const validateAutoDiscountRule = (rule: unknown, index?: number): string[] => {
  const prefix = index === undefined ? '' : `Regula #${index + 1}: `;
  const errors: string[] = [];
  if (typeof rule !== 'object' || rule === null || Array.isArray(rule)) {
    return [`${prefix}regula musi byc obiektem.`];
  }
  const candidate = rule as Partial<AutoDiscountRule>;

  if (!isInteger(candidate.id) || (candidate.id as number) < 1) {
    errors.push(`${prefix}pole "id" musi byc dodatnia liczba calkowita.`);
  }
  if (candidate.type !== 'days' && candidate.type !== 'cartValue') {
    errors.push(`${prefix}pole "type" musi miec wartosc "days" albo "cartValue".`);
  }
  if (!isInteger(candidate.threshold)) {
    errors.push(`${prefix}pole "threshold" musi byc liczba calkowita.`);
  } else if ((candidate.threshold as number) < 0) {
    errors.push(`${prefix}prog nie moze byc ujemny.`);
  } else if (candidate.type === 'days' && (candidate.threshold as number) < 1) {
    errors.push(`${prefix}prog liczby dni musi wynosic co najmniej 1.`);
  }
  if (!isInteger(candidate.percentage)) {
    errors.push(`${prefix}pole "percentage" musi byc liczba calkowita (500 = 5%).`);
  } else if ((candidate.percentage as number) <= 0 || (candidate.percentage as number) > AUTO_DISCOUNT_FULL_PERCENTAGE) {
    errors.push(`${prefix}rabat musi miescic sie w zakresie od 0% do 100%.`);
  }
  if (typeof candidate.active !== 'boolean') {
    errors.push(`${prefix}pole "active" musi byc wartoscia logiczna.`);
  }

  const hasDateFrom = candidate.dateFrom !== undefined && candidate.dateFrom !== null && `${candidate.dateFrom}`.trim() !== '';
  const hasDateTo = candidate.dateTo !== undefined && candidate.dateTo !== null && `${candidate.dateTo}`.trim() !== '';
  const from = hasDateFrom ? parseWallClock(candidate.dateFrom) : undefined;
  const to = hasDateTo ? parseWallClock(candidate.dateTo, true) : undefined;
  if (hasDateFrom && from === undefined) {
    errors.push(`${prefix}nieprawidlowa data rozpoczecia promocji.`);
  }
  if (hasDateTo && to === undefined) {
    errors.push(`${prefix}nieprawidlowa data zakonczenia promocji.`);
  }
  if (from !== undefined && to !== undefined && from >= to) {
    errors.push(`${prefix}data rozpoczecia musi byc wczesniejsza niz data zakonczenia.`);
  }

  return errors;
};

export const validateAutoDiscountRules = (rules: unknown): string[] => {
  if (!Array.isArray(rules)) {
    return ['Konfiguracja rabatow musi byc lista regul.'];
  }
  const errors = rules.flatMap((rule, index) => validateAutoDiscountRule(rule, index));
  const ids = rules
    .map((rule) => (typeof rule === 'object' && rule !== null ? (rule as Partial<AutoDiscountRule>).id : undefined))
    .filter((id) => isInteger(id)) as number[];
  const duplicated = ids.filter((id, index) => ids.indexOf(id) !== index);
  if (duplicated.length > 0) {
    errors.push(`Identyfikatory regul musza byc unikalne (powtorzone: ${Array.from(new Set(duplicated)).join(', ')}).`);
  }
  return errors;
};

export const validateAutoDiscountRulesJSON = (value: string | null | undefined): string[] => {
  if (value === undefined || value === null || `${value}`.trim() === '') {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(`${value}`);
  } catch (e) {
    return ['Konfiguracja rabatow nie jest poprawnym dokumentem JSON.'];
  }
  return validateAutoDiscountRules(parsed);
};

const normalizeRule = (rule: AutoDiscountRule): AutoDiscountRule => ({
  id: rule.id,
  type: rule.type,
  threshold: rule.threshold,
  percentage: rule.percentage,
  dateFrom: rule.dateFrom === undefined || rule.dateFrom === null || `${rule.dateFrom}`.trim() === '' ? null : `${rule.dateFrom}`.trim(),
  dateTo: rule.dateTo === undefined || rule.dateTo === null || `${rule.dateTo}`.trim() === '' ? null : `${rule.dateTo}`.trim(),
  active: rule.active,
});

/**
 * Bezpieczny odczyt konfiguracji. Reguly niespelniajace kontraktu sa pomijane,
 * dzieki czemu bledny wpis nie blokuje skladania zamowien (walidacja przy
 * zapisie ustawienia nie dopuszcza takich danych).
 */
export const parseAutoDiscountRules = (value: string | null | undefined): AutoDiscountRule[] => {
  if (value === undefined || value === null || `${value}`.trim() === '') {
    return [];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(`${value}`);
  } catch (e) {
    return [];
  }
  if (!Array.isArray(parsed)) {
    return [];
  }
  return parsed
    .filter((rule) => validateAutoDiscountRule(rule).length === 0)
    .map((rule) => normalizeRule(rule as AutoDiscountRule));
};

export const isAutoDiscountRuleActive = (rule: AutoDiscountRule, now: Date): boolean => {
  if (!rule.active) {
    return false;
  }
  const nowTime = now.getTime();
  const from = parseWallClock(rule.dateFrom);
  if (from !== undefined && nowTime < from) {
    return false;
  }
  const to = parseWallClock(rule.dateTo, true);
  if (to !== undefined && nowTime > to) {
    return false;
  }
  return true;
};

const isTypeEnabled = (config: AutoDiscountConfig, type: AutoDiscountType): boolean =>
  config.enabled && (type === 'days' ? config.daysEnabled : config.valueEnabled);

/** Reguly aktywne czasowo i wlaczone w konfiguracji (bez sprawdzania progu). */
export const getEnabledAutoDiscountRules = (config: AutoDiscountConfig, now: Date): AutoDiscountRule[] =>
  (config.rules ?? []).filter((rule) => isTypeEnabled(config, rule.type) && isAutoDiscountRuleActive(rule, now));

const isThresholdReached = (rule: AutoDiscountRule, input: AutoDiscountInput): boolean =>
  rule.type === 'days'
    ? input.daysCount >= rule.threshold
    : input.cartValue >= rule.threshold;

/**
 * Deterministyczny wybor najkorzystniejszej reguly:
 * 1. wyzszy procent rabatu,
 * 2. przy remisie: kryterium wartosci koszyka przed kryterium liczby dni,
 * 3. przy dalszym remisie: nizsze id.
 * Wynikowa cena jest identyczna w kazdym z tych przypadkow.
 */
const compareRules = (a: AutoDiscountRule, b: AutoDiscountRule): number => {
  if (a.percentage !== b.percentage) {
    return b.percentage - a.percentage;
  }
  if (a.type !== b.type) {
    return a.type === 'cartValue' ? -1 : 1;
  }
  return a.id - b.id;
};

export const selectAutoDiscount = (config: AutoDiscountConfig, input: AutoDiscountInput): AutoDiscountSelection | undefined => {
  const candidates = getEnabledAutoDiscountRules(config, input.now).filter((candidate) => isThresholdReached(candidate, input));
  if (candidates.length === 0) {
    return undefined;
  }
  const rule = candidates.reduce((best, current) => (compareRules(best, current) <= 0 ? best : current));
  return { rule, percentage: rule.percentage };
};

/** Cena pozycji po rabacie - wspolna dla koszyka i zapisu zamowienia. */
export const applyAutoDiscountToPrice = (price: number, percentage: number): number =>
  Math.round(price * (1 - percentage / AUTO_DISCOUNT_FULL_PERCENTAGE));

/**
 * Podpowiedzi o najblizszym korzystniejszym progu - maksymalnie jedna na
 * kryterium. Progi dajace rabat nie wiekszy niz aktualny sa pomijane, zeby nie
 * zachecac do zakupow bez realnej korzysci. Kolejnosc jest stala (najpierw
 * wartosc koszyka, potem liczba dni), bo obu wartosci "missing" nie da sie
 * porownac - sa w innych jednostkach.
 */
export const getAutoDiscountHints = (
  config: AutoDiscountConfig,
  input: AutoDiscountInput,
  currentPercentage = 0
): AutoDiscountHint[] => {
  const types: AutoDiscountType[] = ['cartValue', 'days'];
  return types
    .map((type) => {
      const candidates = getEnabledAutoDiscountRules(config, input.now)
        .filter((rule) => rule.type === type)
        .filter((rule) => rule.percentage > currentPercentage)
        .filter((rule) => !isThresholdReached(rule, input))
        .map((rule) => ({
          type,
          percentage: rule.percentage,
          threshold: rule.threshold,
          missing: type === 'days' ? rule.threshold - input.daysCount : rule.threshold - input.cartValue,
        }));
      if (candidates.length === 0) {
        return undefined;
      }
      return candidates.reduce((best, current) => {
        if (current.missing !== best.missing) {
          return current.missing < best.missing ? current : best;
        }
        return current.percentage > best.percentage ? current : best;
      });
    })
    .filter((hint): hint is AutoDiscountHint => hint !== undefined);
};
